import React from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/Navbar/Navbar';
import Footer from '../../components/Footer/Footer';
import {
  Check, Star, Zap, TrendingUp, Award, Sparkles, Package,
  RotateCcw, XCircle, List, Layers, Megaphone, CreditCard, Warehouse, ChevronRight
} from 'lucide-react';
import './AdvertisingHubPage.css';

export default function AdvertisingHubPage() {
  const navigate = useNavigate();

  const handleSelectPlan = (planName, price, duration = '1 Month') => {
    navigate('/ad-checkout', { state: { planName, price, duration } });
  };

  return (
    <div className="adv-hub-wrapper">
      <Navbar />

      {/* SECTION 1: HERO (Light blue background) */}
      <section className="adv-hero-section light-blue-bg">
        <div className="container adv-hero-grid-2">
          <div className="hero-copy">
            <span className="eyebrow-text">Learn How to Sell Online</span>
            <h1 className="hero-heading">
              Grow Your Business with Flexible Seller Plans
            </h1>
            <div className="hero-callout-simple">
              <span className="new-badge-pill">NEW</span>
              <div className="adv-gst-callout-text">
                <div className="adv-gst-callout-line">Don't have a GSTIN or have a Composition GSTIN?</div>
                <div className="adv-gst-callout-line">You can still sell on Haatza. Click <a href="/donothavegst" className="adv-gst-callout-link">here</a> to know more.</div>
              </div>
            </div>
            <a href="/signin" style={{ textDecoration: 'none' }}>
              <button className="hero-cta-btn">Start Selling</button>
            </a>
          </div>

          <div className="hero-phone-container">
            <img
              src="https://static.wixstatic.com/media/73d0ef_5fb9d11e9b94405d8ccc671c484b9a84~mv2.png/v1/fill/w_591,h_496,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/Haatza%20pricining.png"
              alt="Learn How to Sell Online"
              className="hero-adv-img"
            />
          </div>
        </div>
      </section>

      {/* SECTION 2: CHOOSE YOUR PRICING PLAN */}
      <section className="pricing-plans-section">
        <div className="container">
          <h2 className="section-h2 pricing-title-center">Choose your pricing plan</h2>

          <div className="plans-cards-grid-3">

            {/* Card 1 — Growth */}
            <div className="plan-card shadow-sm">
              <div className="plan-header">
                <h3 className="plan-name">Growth</h3>
                <div className="plan-price-row">
                  <span className="currency">₹</span>
                  <span className="price-num">999</span>
                </div>
                <span className="validity-tag">Valid for one month</span>
              </div>

              <button
                className="plan-sub-btn"
                onClick={() => handleSelectPlan('Growth', '₹999', '1 Month')}
              >
                Subscription
              </button>

              <ul className="plan-features-list">
                <li><Check size={16} className="check-icon text-blue-600" /> Seller Verified Badge</li>
                <li><Check size={16} className="check-icon text-blue-600" /> Featured placement in category + search (5 SKUs)</li>
                <li><Check size={16} className="check-icon text-blue-600" /> "Promoted Seller" tag in app</li>
                <li><Check size={16} className="check-icon text-blue-600" /> ₹500 Ad Credit (Google + Meta)</li>
                <li><Check size={16} className="check-icon text-blue-600" /> Basic analytics dashboard</li>
                <li><Check size={16} className="check-icon text-blue-600" /> AI Product Title & Description Optimizer</li>
                <li><Check size={16} className="check-icon text-blue-600" /> SEO Score for each product</li>
                <li><Check size={16} className="check-icon text-blue-600" /> In-App Product Promotions</li>
                <li><Check size={16} className="check-icon text-blue-600" /> Auto Keyword Suggestions for trending searches</li>
              </ul>

              <div className="boost-badge">
                📈 Expected boost: <strong>up to 1.3× sales</strong>
              </div>
            </div>

            {/* Card 2 — Pro (Recommended - Elevated) */}
            <div className="plan-card plan-card-recommended shadow-lg">
              <div className="recommended-ribbon"> Recommended</div>

              <div className="plan-header">
                <h3 className="plan-name text-blue-600">Pro</h3>
                <div className="plan-price-row">
                  <span className="currency">₹</span>
                  <span className="price-num">1,999</span>
                </div>
                <span className="validity-tag">Valid for one month</span>
              </div>

              <button
                className="plan-sub-btn recommended-btn"
                onClick={() => handleSelectPlan('Pro', '₹1,999', '1 Month')}
              >
                Subscription
              </button>

              <ul className="plan-features-list">
                <li><Check size={16} className="check-icon text-blue-600" /> Seller Verified Badge</li>
                <li><Check size={16} className="check-icon text-blue-600" /> Featured placement for 15 SKUs</li>
                <li><Check size={16} className="check-icon text-blue-600" /> Priority "Trending Now" visibility</li>
                <li><Check size={16} className="check-icon text-blue-600" /> ₹1,200 Ad Credit (Google + Meta)</li>
                <li><Check size={16} className="check-icon text-blue-600" /> 1 Managed digital ad campaign/month</li>
                <li><Check size={16} className="check-icon text-blue-600" /> Advanced Analytics</li>
                <li><Check size={16} className="check-icon text-blue-600" /> AI Auto-Pricing Suggestions</li>
                <li><Check size={16} className="check-icon text-blue-600" /> In-App Product Promotions</li>
                <li><Check size={16} className="check-icon text-blue-600" /> Cross-Sell Recommendation Engine</li>
                <li><Check size={16} className="check-icon text-blue-600" /> Early Payout/Settlement (T+1)</li>
                <li><Check size={16} className="check-icon text-blue-600" /> Priority logistics partners</li>
              </ul>

              <div className="boost-badge recommended-boost">
                📈 Expected boost: <strong>up to 2× sales</strong>
              </div>
            </div>
            {/* Card 3 — Enterprise */}
            <div className="plan-card shadow-sm">
              <div className="plan-header">
                <h3 className="plan-name">Enterprise</h3>
                <div className="plan-price-row">
                  <span className="currency">₹</span>
                  <span className="price-num">2,499</span>
                </div>
                <span className="validity-tag">Valid for one month</span>
              </div>

              <button
                className="plan-sub-btn"
                onClick={() => handleSelectPlan('Enterprise', '₹2,499', '1 Month')}
              >
                Subscription
              </button>

              <ul className="plan-features-list">
                <li><Check size={16} className="check-icon text-blue-600" /> Seller Verified Badge</li>
                <li><Check size={16} className="check-icon text-blue-600" /> Featured placement on all SKUs</li>
                <li><Check size={16} className="check-icon text-blue-600" /> "Top Seller" premium badge in listings</li>
                <li><Check size={16} className="check-icon text-blue-600" /> ₹2,000 Ad Credit</li>
                <li><Check size={16} className="check-icon text-blue-600" /> 2 Managed ad campaigns/month</li>
                <li><Check size={16} className="check-icon text-blue-600" /> Flash Sale Tools</li>
                <li><Check size={16} className="check-icon text-blue-600" /> Search Result Boosting</li>
                <li><Check size={16} className="check-icon text-blue-600" /> In-App Product Promotions</li>
                <li><Check size={16} className="check-icon text-blue-600" /> Detailed conversion funnel analytics</li>
                <li><Check size={16} className="check-icon text-blue-600" /> Cart Abandonment Notifications</li>
                <li><Check size={16} className="check-icon text-blue-600" /> Repeat Buyer Retargeting (push / in-app)</li>
              </ul>

              <div className="boost-badge">
                📈 Expected boost: <strong>up to 3× sales</strong>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* SECTION 3: MORE THAN PRICING. IT'S A GROWTH PARTNER. */}
      <section className="growth-partner-section">
        <div className="container">
          <div className="growth-partner-grid">
            <div className="growth-copy-side">
              <h2 className="section-h2">
                More Than Pricing. It's a <span className="highlight-blue">Growth Partner.</span>
              </h2>
              <p className="section-intro-text">
                At Haatza, our goal isn't just to give you a pricing plan—it's to empower you to succeed as a seller. Here's what makes our plans the best choice:
              </p>
              <div className="features-grid-2x2">
                <div className="partner-feature-card">
                  <div className="partner-card-header">
                    <img src="https://static.wixstatic.com/media/fe689e_2378ac6785324d6a87177ac4caf8f209~mv2.jpg/v1/fill/w_86,h_64,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/Screenshot%202025-01-22%20at%209_39_edited.jpg" alt="No Hidden Fees" className="partner-icon-img" />
                    <h4 className="partner-title">No Hidden Fees</h4>
                  </div>
                  <p className="partner-desc">Transparent pricing you can trust.</p>
                </div>

                <div className="partner-feature-card">
                  <div className="partner-card-header">
                    <img src="https://static.wixstatic.com/media/fe689e_ff13d5ee9bf94121ace155d8b35f59a5~mv2.jpg/v1/fill/w_67,h_66,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/Screenshot%202025-01-22%20at%209_38_edited.jpg" alt="Smart Tools" className="partner-icon-img" />
                    <h4 className="partner-title">Smart Tools</h4>
                  </div>
                  <p className="partner-desc">Analytics, reporting & insights to grow faster.</p>
                </div>

                <div className="partner-feature-card">
                  <div className="partner-card-header">
                    <img src="https://static.wixstatic.com/media/fe689e_4cfc4802e14e41788b16b482383ba722~mv2.png/v1/fill/w_80,h_66,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/Screenshot%202025-01-08%20at%207_edited.png" alt="Reach More Buyers" className="partner-icon-img" />
                    <h4 className="partner-title">Reach More Buyers</h4>
                  </div>
                  <p className="partner-desc">Nationwide visibility for your products.</p>
                </div>

                <div className="partner-feature-card">
                  <div className="partner-card-header">
                    <img src="https://static.wixstatic.com/media/fe689e_001b74395f754eceb1daba58582d901a~mv2.png/v1/fill/w_64,h_66,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/Screenshot%202025-01-08%20at%207_edited.png" alt="Seller First Support" className="partner-icon-img" />
                    <h4 className="partner-title">Seller First Support</h4>
                  </div>
                  <p className="partner-desc">Dedicated help to guide you every step.</p>
                </div>
              </div>
            </div>

            <div className="growth-visual-side">
              <img
                src="https://static.wixstatic.com/media/73d0ef_f00f1816fca047c5a3775a455475f1a5~mv2.jpg/v1/fill/w_450,h_675,fp_0.42_0.05,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/cdsds_edited.jpg"
                alt="Haatza Smiling Delivery Representative"
                className="delivery-rep-img"
              />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}